<template>
	<div class="login-page">
		<div class="bg-orb orb-a"></div>
		<div class="bg-orb orb-b"></div>
		<div class="bg-grid"></div>

		<div class="login-shell">
			<section class="brand-panel">
				<div class="brand-badge">NBlog</div>
				<h1>欢迎回来</h1>
				<p>登录后可发布博客、管理动态、审核评论。</p>
				<div class="brand-card">
					<img src="/img/avatar.jpg" alt="avatar">
					<div>
						<strong>账号工作台</strong>
						<span>安全访问管理功能</span>
					</div>
				</div>
			</section>

			<section class="form-panel">
				<div class="panel-head">
					<h2>账号登录</h2>
					<p>使用后台账号继续操作</p>
				</div>

				<form class="login-form" @submit.prevent="handleLogin">
					<label for="username">用户名</label>
					<div class="input-wrap">
						<i class="el-icon-user-solid"></i>
						<input
							id="username"
							v-model.trim="loginForm.username"
							type="text"
							autocomplete="username"
							placeholder="请输入用户名"
							maxlength="60"
							required
						>
					</div>

					<label for="password">密码</label>
					<div class="input-wrap">
						<i class="el-icon-lock"></i>
						<input
							id="password"
							v-model="loginForm.password"
							type="password"
							autocomplete="current-password"
							placeholder="请输入密码"
							maxlength="100"
							required
							@keyup.enter="handleLogin"
						>
					</div>

					<div class="form-row">
						<label class="remember-checkbox">
							<input v-model="rememberMe" type="checkbox">
							<span>记住用户名</span>
						</label>
						<button type="button" class="ghost-btn" @click="resetLoginForm">清空</button>
					</div>

					<button class="login-btn" type="submit" :disabled="!canSubmit || isLoading">
						{{ isLoading ? '登录中...' : '登录' }}
					</button>
				</form>
			</section>
		</div>
	</div>
</template>

<script>
import {login} from '@/api/login'
import {setViewAuthFromLogin} from '@/util/viewAuth'

const REMEMBER_KEY = 'nblog_view_remember_username'

export default {
	name: 'MyLogin',
	data() {
		return {
			loginForm: {
				username: '',
				password: '',
			},
			rememberMe: false,
			isLoading: false,
		}
	},
	computed: {
		canSubmit() {
			return !!(this.loginForm.username && this.loginForm.password)
		}
	},
	mounted() {
		this.restoreRememberedUsername()
	},
	methods: {
		resolveRedirect(defaultPath = '/home') {
			const raw = typeof this.$route.query.redirect === 'string' ? this.$route.query.redirect : ''
			if (!raw) {
				return defaultPath
			}
			try {
				return decodeURIComponent(raw)
			} catch (e) {
				return raw
			}
		},
		restoreRememberedUsername() {
			const remembered = window.localStorage.getItem(REMEMBER_KEY)
			if (!remembered) {
				return
			}
			this.loginForm.username = remembered
			this.rememberMe = true
		},
		persistRememberedUsername() {
			if (this.rememberMe && this.loginForm.username) {
				window.localStorage.setItem(REMEMBER_KEY, this.loginForm.username)
			} else {
				window.localStorage.removeItem(REMEMBER_KEY)
			}
		},
		resetLoginForm() {
			this.loginForm.password = ''
			if (!this.rememberMe) {
				this.loginForm.username = ''
			}
		},
		handleLogin() {
			if (!this.canSubmit || this.isLoading) {
				return
			}
			this.isLoading = true
			const payload = {
				username: this.loginForm.username.trim(),
				password: this.loginForm.password
			}

			login(payload).then(res => {
				if (res.code !== 200) {
					this.msgError(res.msg || '登录失败')
					return
				}

				setViewAuthFromLogin(res.data, payload.username)
				this.persistRememberedUsername()

				this.msgSuccess(res.msg || '登录成功')
				const redirect = this.resolveRedirect('/home')
				this.$router.replace(redirect)
			}).catch(() => {
				this.msgError('请求失败')
			}).finally(() => {
				this.isLoading = false
			})
		}
	}
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

.login-page {
	position: relative;
	min-height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	overflow: hidden;
	font-family: 'Manrope', 'PingFang SC', 'Microsoft YaHei', sans-serif;
	background: linear-gradient(135deg, #eef6ff 0%, #f8fbff 45%, #f1f8f7 100%);
}

.bg-grid {
	position: absolute;
	inset: 0;
	background-image: linear-gradient(rgba(59, 130, 246, .08) 1px, transparent 1px),
	linear-gradient(90deg, rgba(59, 130, 246, .08) 1px, transparent 1px);
	background-size: 26px 26px;
	mask-image: radial-gradient(circle at center, black 30%, transparent 88%);
	pointer-events: none;
}

.bg-orb {
	position: absolute;
	border-radius: 50%;
	filter: blur(10px);
	pointer-events: none;
}

.orb-a {
	width: 360px;
	height: 360px;
	left: -90px;
	top: -60px;
	background: radial-gradient(circle, rgba(14, 165, 233, .26) 0%, rgba(14, 165, 233, 0) 70%);
}

.orb-b {
	width: 420px;
	height: 420px;
	right: -120px;
	bottom: -100px;
	background: radial-gradient(circle, rgba(16, 185, 129, .20) 0%, rgba(16, 185, 129, 0) 72%);
}

.login-shell {
	position: relative;
	z-index: 2;
	width: min(900px, 100%);
	display: grid;
	grid-template-columns: 1.08fr 1fr;
	border-radius: 22px;
	overflow: hidden;
	box-shadow: 0 18px 45px rgba(15, 23, 42, .13);
	background: #ffffff;
	border: 1px solid #e5edf6;
}

.brand-panel {
	padding: 44px 38px;
	background: linear-gradient(140deg, #0f172a 0%, #1d3557 45%, #0b7285 100%);
	color: #fff;
	display: flex;
	flex-direction: column;
}

.brand-badge {
	display: inline-flex;
	width: fit-content;
	padding: 4px 10px;
	border-radius: 999px;
	font-size: 12px;
	letter-spacing: .4px;
	background: rgba(255, 255, 255, .14);
}

.brand-panel h1 {
	margin: 18px 0 8px;
	font-size: 34px;
	line-height: 1.05;
	font-weight: 800;
}

.brand-panel p {
	margin: 0;
	font-size: 14px;
	line-height: 1.7;
	color: rgba(255, 255, 255, .9);
	max-width: 280px;
}

.brand-card {
	margin-top: auto;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px;
	border-radius: 14px;
	background: rgba(255, 255, 255, .12);
	border: 1px solid rgba(255, 255, 255, .24);
}

.brand-card img {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	object-fit: cover;
	border: 2px solid rgba(255, 255, 255, .5);
}

.brand-card strong {
	display: block;
	font-size: 14px;
}

.brand-card span {
	font-size: 12px;
	color: rgba(255, 255, 255, .86);
}

.form-panel {
	padding: 40px 36px;
	display: flex;
	flex-direction: column;
	justify-content: center;
}

.panel-head h2 {
	margin: 0;
	font-size: 26px;
	color: #0f172a;
	font-weight: 800;
}

.panel-head p {
	margin: 6px 0 20px;
	font-size: 13px;
	color: #64748b;
}

.login-form {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.login-form label {
	font-size: 13px;
	font-weight: 600;
	color: #334155;
}

.input-wrap {
	display: flex;
	align-items: center;
	gap: 8px;
	border: 1px solid #dbe5ef;
	border-radius: 10px;
	padding: 10px 12px;
	transition: border-color .2s ease, box-shadow .2s ease;
	background: #fff;
}

.input-wrap:focus-within {
	border-color: #38bdf8;
	box-shadow: 0 0 0 3px rgba(56, 189, 248, .2);
}

.input-wrap i {
	font-size: 16px;
	color: #64748b;
}

.input-wrap input {
	width: 100%;
	border: 0;
	outline: 0;
	font-size: 14px;
	color: #0f172a;
	background: transparent;
}

.input-wrap input::placeholder {
	color: #94a3b8;
}

.form-row {
	margin-top: 6px;
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.remember-checkbox {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	color: #475569;
	user-select: none;
}

.remember-checkbox input {
	width: 14px;
	height: 14px;
}

.ghost-btn {
	border: 0;
	background: transparent;
	color: #0ea5e9;
	font-size: 13px;
	cursor: pointer;
	padding: 0;
}

.ghost-btn:hover {
	color: #0284c7;
}

.login-btn {
	margin-top: 14px;
	border: 0;
	border-radius: 11px;
	height: 44px;
	font-size: 15px;
	font-weight: 700;
	color: #fff;
	background: linear-gradient(90deg, #0284c7 0%, #06b6d4 100%);
	cursor: pointer;
	transition: transform .2s ease, box-shadow .2s ease, opacity .2s ease;
}

.login-btn:hover:enabled {
	transform: translateY(-1px);
	box-shadow: 0 8px 18px rgba(14, 165, 233, .35);
}

.login-btn:disabled {
	opacity: .68;
	cursor: not-allowed;
}

@media (max-width: 900px) {
	.login-shell {
		grid-template-columns: 1fr;
		max-width: 460px;
	}

	.brand-panel {
		padding: 24px 22px;
	}

	.brand-panel h1 {
		font-size: 28px;
	}

	.brand-card {
		margin-top: 18px;
	}

	.form-panel {
		padding: 26px 22px 24px;
	}
}

@media (max-width: 520px) {
	.login-page {
		padding: 12px;
	}

	.panel-head h2 {
		font-size: 22px;
	}
}
</style>
